import { useCallback } from 'react';
import { RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface DateRangePickerProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  className?: string;
}

function getMonthStart(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
}

function getMonthEnd(date: Date): string {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const lastDay = new Date(y, m, 0).getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

export function DateRangePicker({ from, to, onChange, className = '' }: DateRangePickerProps) {
  const handleFromChange = useCallback(
    (newFrom: string) => {
      if (newFrom > to) {
        onChange(newFrom, newFrom);
      } else {
        onChange(newFrom, to);
      }
    },
    [to, onChange]
  );

  const handleToChange = useCallback(
    (newTo: string) => {
      if (newTo < from) {
        onChange(newTo, newTo);
      } else {
        onChange(from, newTo);
      }
    },
    [from, onChange]
  );

  const handleQuickSelect = useCallback(
    (type: 'thisMonth' | 'lastMonth' | 'thisYear') => {
      const now = new Date();
      switch (type) {
        case 'thisMonth':
          onChange(getMonthStart(now), getMonthEnd(now));
          break;
        case 'lastMonth': {
          const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          onChange(getMonthStart(last), getMonthEnd(last));
          break;
        }
        case 'thisYear':
          onChange(`${now.getFullYear()}-01-01`, `${now.getFullYear()}-12-31`);
          break;
      }
    },
    [onChange]
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Zeitraum:</span>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={from}
            onChange={(e) => handleFromChange(e.target.value)}
            className="w-auto h-9"
            aria-label="Zeitraum Start"
          />
          <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">—</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => handleToChange(e.target.value)}
            className="w-auto h-9"
            aria-label="Zeitraum Ende"
          />
          <Button
            onClick={() => handleQuickSelect('thisMonth')}
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            title="Auf aktuellen Monat zurücksetzen"
            aria-label="Auf aktuellen Monat zurücksetzen"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            onClick={() => handleQuickSelect('thisMonth')}
            variant="outline"
            size="sm"
            className="h-7 rounded-full text-xs"
          >
            Dieser Monat
          </Button>
          <Button
            onClick={() => handleQuickSelect('lastMonth')}
            variant="outline"
            size="sm"
            className="h-7 rounded-full text-xs"
          >
            Letzter Monat
          </Button>
          <Button
            onClick={() => handleQuickSelect('thisYear')}
            variant="outline"
            size="sm"
            className="h-7 rounded-full text-xs"
          >
            Dieses Jahr
          </Button>
        </div>
      </div>
    </div>
  );
}
